import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Banknote,
  Briefcase,
  Building2,
  Eye,
  FileText,
  Home,
  Info,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  User,
  Wallet,
} from 'lucide-react'

import type {
  BankListItem,
  Contract,
  EmpAddressListItem,
  EmpDocumentListItem,
  Employee,
  MediaLinksVm,
  MedicalVm,
  SkillListItem,
} from '@/api/types'
import { formatCurrency } from '@/lib/format'
import {
  displayValue,
  employeeFullName,
  employeeInitials,
  isBlank,
  profileDate,
  resolveMediaUrl,
} from '@/modules/employees/employeeDisplay'

function Row({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value?: React.ReactNode
  icon?: React.ComponentType<{ className?: string }>
}) {
  const empty = value === undefined || value === null || value === ''
  return (
    <div className="emp-profile-kv flex items-center gap-3 py-[9px] border-b border-slate-100 last:border-0">
      {Icon ? <Icon className="h-4 w-4 text-sky-500 shrink-0" /> : null}
      <span className="text-[13px] text-slate-500 shrink-0">{label}</span>
      <span className="ml-auto text-[13px] font-semibold text-slate-800 text-right break-words max-w-[58%]">
        {empty ? '—' : value}
      </span>
    </div>
  )
}

function ProfileCard({
  title,
  icon: Icon,
  iconWrap,
  children,
}: {
  title: string
  icon: React.ComponentType<{ className?: string }>
  iconWrap: string
  children: React.ReactNode
}) {
  return (
    <section className="emp-profile-card rounded-2xl bg-white border border-slate-100 p-5 shadow-[0_8px_24px_rgba(15,23,42,0.04)] print:shadow-none">
      <div className="flex items-center gap-2.5 mb-3">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${iconWrap}`}>
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="text-[15px] font-semibold text-slate-800">{title}</h2>
      </div>
      {children}
    </section>
  )
}

function composeAddress(addr?: EmpAddressListItem): string {
  if (!addr) return ''
  return [addr.building, addr.street, addr.district, addr.apartment, addr.addressDetail]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter((p) => p && !isBlank(p))
    .join(', ')
}

interface EmployeeProfileCardsProps {
  employee: Employee
  qrDataUrl: string
  address?: EmpAddressListItem
  extraAddress?: EmpAddressListItem
  addressCountry?: string
  addressCity?: string
  medical?: MedicalVm
  media?: MediaLinksVm
  contract?: Contract | null
  bank?: BankListItem
  documents: EmpDocumentListItem[]
  skills: SkillListItem[]
  shiftLabel?: string
  maritalName?: string
  noticePeriodName?: string
  statusName?: string
}

export const EmployeeProfileCards: React.FC<EmployeeProfileCardsProps> = ({
  employee,
  qrDataUrl,
  address,
  extraAddress,
  addressCountry,
  addressCity,
  medical,
  media,
  contract,
  bank,
  documents,
  skills,
  shiftLabel,
  maritalName,
  noticePeriodName,
  statusName,
}) => {
  const { t } = useTranslation('common')
  const name = employeeFullName(employee)
  const isActive = employee.isActive === 1
  const statusLabel = statusName || (isActive ? t('employees.profile.active') : t('employees.profile.inactive'))
  const linkedIn = media?.linkedInLink || media?.linkedinLink
  const currentAddress = composeAddress(address) || (!isBlank(employee.empAddress) ? String(employee.empAddress) : '')
  const permanentAddress = composeAddress(extraAddress)
  const location = !isBlank(employee.empPostedLocation) ? String(employee.empPostedLocation) : ''
  const salarySource = (contract || {}) as Contract & Record<string, unknown>
  const salaryAmount = (...keys: string[]) => {
    for (const key of keys) {
      const value = salarySource[key]
      if (value === undefined || value === null || value === '') continue
      const amount = Number(value)
      if (!Number.isNaN(amount)) return amount
    }
    return 0
  }
  const basic = salaryAmount('basicSalary', 'BasicSalary')
  const housing = salaryAmount('housingAllowance', 'HousingAllowance')
  const transport = salaryAmount(
    'transpotationAllowance',
    'TranspotationAllowance',
    'transportationAllowance',
    'TransportationAllowance'
  )
  const meal = salaryAmount('mealAllowance', 'MealAllowance')
  const additionalJob = salaryAmount('additionalJobSalary', 'AdditionalJobSalary')
  const total =
    salaryAmount('totalContractSalary', 'TotalContractSalary', 'totalSalary', 'TotalSalary') ||
    basic + housing + transport + meal + additionalJob
  const joining = profileDate(employee.joiningDate || contract?.contractStartDate)
  const probation = contract?.probationPeriod
  const photo = resolveMediaUrl(employee.imageUrl || employee.profileImageUrl)
  const job = employee.jobName || ''
  const department = employee.departmentName || ''

  return (
    <div className="employee-profile-page space-y-5">
      <section className="employee-profile-hero rounded-2xl bg-gradient-to-r from-[#eef7ff] via-white to-white border border-slate-100 px-6 py-5 shadow-[0_8px_24px_rgba(15,23,42,0.04)] print:shadow-none">
        <div className="employee-profile-hero-grid grid grid-cols-1 lg:grid-cols-[108px_minmax(0,1fr)_auto_230px] gap-5 items-center">
          <div className="h-[108px] w-[108px] rounded-2xl overflow-hidden bg-slate-100 ring-4 ring-white shadow-md shrink-0">
            {photo ? (
              <img src={photo} alt={name} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-2xl font-semibold text-slate-500">
                {employeeInitials(employee)}
              </div>
            )}
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[26px] leading-tight font-bold text-slate-900">{name}</h1>
              <span className="inline-flex items-center rounded-full bg-emerald-500 px-2.5 py-0.5 text-[11px] font-semibold text-white">
                {statusLabel}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {employee.empCode}
              {job ? <span className="text-slate-400">  |  {job}</span> : null}
            </p>
            {(department || job) && (
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[13px] text-slate-600">
                {department ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="h-4 w-4 text-slate-400" /> {department}
                  </span>
                ) : null}
                {job ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase className="h-4 w-4 text-slate-400" /> {job}
                  </span>
                ) : null}
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-[13px] text-slate-600">
              {!isBlank(employee.empEmail) ? (
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-4 w-4 text-slate-400" /> {employee.empEmail}
                </span>
              ) : null}
              {!isBlank(employee.empMobileNo) ? (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-slate-400" /> {employee.empMobileNo}
                </span>
              ) : null}
              {location ? (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-slate-400" /> {location}
                </span>
              ) : null}
            </div>
          </div>

          {qrDataUrl ? (
            <div className="justify-self-center xl:justify-self-end text-center">
              <div className="rounded-xl border border-slate-200 bg-white p-2">
                <img src={qrDataUrl} alt={employee.empCode || 'QR'} className="emp-profile-qr h-[92px] w-[92px]" />
              </div>
              <p className="text-[11px] text-slate-500 mt-1">{employee.empCode}</p>
            </div>
          ) : (
            <div />
          )}

          <div className="space-y-2.5 text-[13px] min-w-[220px]">
            {joining ? (
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">{t('employees.profile.dateOfJoining')}</span>
                <span className="font-semibold text-slate-800">{joining}</span>
              </div>
            ) : null}
            {location ? (
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">{t('employees.profile.workLocation')}</span>
                <span className="font-semibold text-slate-800">{location}</span>
              </div>
            ) : null}
            {probation != null ? (
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">{t('employees.profile.probationPeriod')}</span>
                <span className="font-semibold text-slate-800">
                  {probation} {t('employees.profile.months')}
                </span>
              </div>
            ) : null}
            <div className="flex justify-between gap-4 items-center">
              <span className="text-slate-500">{t('employees.profile.status')}</span>
              <span className="inline-flex items-center rounded-full bg-emerald-500 px-2.5 py-0.5 text-[11px] font-semibold text-white">
                {statusLabel}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="employee-profile-row grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ProfileCard title={t('employees.profile.personalInformation')} icon={User} iconWrap="bg-sky-100 text-sky-600">
          <Row label={t('employees.profile.fullName')} value={name} />
          <Row label={t('employees.profile.fatherName')} value={displayValue(employee.empFatherNameEn || employee.empFatherNameAr)} />
          <Row label={t('employees.profile.dateOfBirth')} value={profileDate(employee.empDob) || '—'} />
          <Row label={t('employees.profile.gender')} value={displayValue(employee.genderName)} />
          <Row label={t('employees.profile.maritalStatus')} value={displayValue(maritalName)} />
          <Row label={t('employees.profile.nationality')} value={displayValue(employee.nationalityName)} />
          <Row label={t('employees.profile.iqamaNo')} value={displayValue(employee.iqamaNo)} />
          <Row label={t('employees.profile.passportNo')} value={displayValue(employee.passportNo)} />
          <Row label={t('employees.profile.bloodGroup')} value={displayValue(medical?.bloodGroup)} />
          <Row label={t('employees.profile.birthPlace')} value={displayValue(employee.empBirthPlace)} />
          <Row label={t('employees.profile.age')} value={displayValue(employee.empAge)} />
        </ProfileCard>

        <ProfileCard title={t('employees.profile.contactInformation')} icon={Phone} iconWrap="bg-cyan-100 text-cyan-600">
          <Row icon={Phone} label={t('employees.profile.personalPhone')} value={displayValue(employee.empMobileNo)} />
          <Row icon={Phone} label={t('employees.profile.officialPhone')} value={displayValue(employee.companyMobileNo)} />
          <Row icon={Mail} label={t('employees.profile.email')} value={displayValue(employee.empEmail)} />
          <Row
            icon={Linkedin}
            label={t('employees.profile.linkedin')}
            value={
              !isBlank(linkedIn) ? (
                <a
                  href={String(linkedIn).startsWith('http') ? String(linkedIn) : `https://${linkedIn}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sky-600 hover:underline"
                >
                  {linkedIn}
                </a>
              ) : '—'
            }
          />
          <Row icon={Phone} label={t('employees.profile.emergencyContact')} value={displayValue(employee.contactPersonNumber || employee.empReferenceContact)} />
        </ProfileCard>

        <ProfileCard title={t('employees.profile.addressInformation')} icon={MapPin} iconWrap="bg-emerald-100 text-emerald-600">
          {currentAddress ? (
            <div className="mb-3 rounded-xl bg-sky-50 px-3 py-3 flex gap-3">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                <Home className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs font-medium text-sky-700">{t('employees.profile.currentAddress')}</p>
                <p className="text-sm font-semibold text-slate-800 leading-snug">{currentAddress}</p>
              </div>
            </div>
          ) : null}
          {permanentAddress ? (
            <div className="mb-3 rounded-xl bg-emerald-50 px-3 py-3 flex gap-3">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                <Home className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs font-medium text-emerald-700">{t('employees.profile.permanentAddress')}</p>
                <p className="text-sm font-semibold text-slate-800 leading-snug">{permanentAddress}</p>
              </div>
            </div>
          ) : null}
          <Row label={t('employees.profile.country')} value={displayValue(addressCountry)} />
          <Row label={t('employees.profile.city')} value={displayValue(addressCity)} />
          <Row label={t('employees.profile.postalCode')} value={displayValue(address?.zipCode)} />
        </ProfileCard>
      </div>

      <div className="employee-profile-row grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ProfileCard title={t('employees.profile.employmentInformation')} icon={Briefcase} iconWrap="bg-violet-100 text-violet-600">
          <Row label={t('employees.profile.employeeId')} value={displayValue(employee.empCode)} />
          <Row label={t('employees.profile.department')} value={displayValue(department)} />
          <Row label={t('employees.profile.designation')} value={displayValue(job)} />
          <Row label={t('employees.profile.dateOfJoining')} value={joining || '—'} />
          <Row
            label={t('employees.profile.probationPeriod')}
            value={probation != null ? `${probation} ${t('employees.profile.months')}` : '—'}
          />
          <Row label={t('employees.profile.workLocation')} value={displayValue(location)} />
          <Row label={t('employees.profile.shift')} value={displayValue(shiftLabel)} />
          <Row label={t('employees.profile.noticePeriod')} value={displayValue(noticePeriodName)} />
          <Row
            label={t('employees.profile.status')}
            value={
              <span className="inline-flex items-center rounded-full bg-emerald-500 px-2.5 py-0.5 text-[11px] font-semibold text-white">
                {statusLabel}
              </span>
            }
          />
        </ProfileCard>

        <div className="emp-profile-stack space-y-5">
          <ProfileCard title={t('employees.profile.salaryInformation')} icon={Wallet} iconWrap="bg-amber-100 text-amber-600">
            {contract ? (
              <>
                <Row label={t('employees.profile.basicSalary')} value={formatCurrency(basic)} />
                <Row label={t('employees.profile.housingAllowance')} value={formatCurrency(housing)} />
                <Row label={t('employees.profile.transportAllowance')} value={formatCurrency(transport)} />
                <Row label={t('employees.profile.mealAllowance')} value={formatCurrency(meal)} />
                {additionalJob > 0 ? (
                  <Row label={t('employees.profile.additionalJobSalary')} value={formatCurrency(additionalJob)} />
                ) : null}
                <div className="mt-3 rounded-xl bg-emerald-50 px-3 py-2.5 flex justify-between items-center text-emerald-800 font-semibold">
                  <span>{t('employees.profile.totalSalary')}</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('employees.profile.noSalary')}</p>
            )}
          </ProfileCard>

          <ProfileCard title={t('employees.profile.documents')} icon={FileText} iconWrap="bg-indigo-100 text-indigo-600">
            {documents.length === 0 ? (
              <p className="text-sm text-slate-500">{t('employees.profile.noDocuments')}</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {documents.map((doc) => {
                  const href = resolveMediaUrl(doc.documentAttachUrl)
                  return (
                    <li key={doc.id} className="flex items-center justify-between gap-2 py-2.5">
                      <span className="inline-flex items-center gap-2 min-w-0 text-[13px] text-slate-800">
                        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="truncate">{doc.documentName || doc.documentTypeName || t('employees.profile.document')}</span>
                      </span>
                      {href ? (
                        <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-600 text-[13px] font-medium shrink-0">
                          <Eye className="h-3.5 w-3.5" />
                          {t('employees.profile.view')}
                        </a>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            )}
          </ProfileCard>
        </div>

        <div className="emp-profile-stack space-y-5">
          <ProfileCard title={t('employees.profile.bankDetails')} icon={Banknote} iconWrap="bg-rose-100 text-rose-600">
            {bank ? (
              <>
                <Row label={t('employees.profile.bankName')} value={displayValue(bank.bankName)} />
                <Row label={t('employees.profile.accountTitle')} value={displayValue(bank.accountTitle)} />
                <Row label={t('employees.profile.accountNo')} value={displayValue(bank.accountNo)} />
                <Row label={t('employees.profile.iban')} value={displayValue(bank.iban)} />
              </>
            ) : (
              <p className="text-sm text-slate-500">{t('employees.profile.noBank')}</p>
            )}
          </ProfileCard>

          <ProfileCard title={t('employees.profile.additionalInformation')} icon={Info} iconWrap="bg-cyan-100 text-cyan-600">
            <Row
              label={t('employees.profile.skills')}
              value={
                skills.length
                  ? skills.map((s) => s.skillTypeName).filter(Boolean).join(', ')
                  : '—'
              }
            />
            <Row label={t('employees.profile.reference')} value={displayValue(employee.empRefrence)} />
            <Row label={t('employees.profile.referenceId')} value={displayValue(employee.empReferenceId)} />
          </ProfileCard>
        </div>
      </div>
    </div>
  )
}
